import React, { useEffect, useState } from 'react';
import { injectIntl, IntlShape } from 'react-intl';
import {
  Card,
  CardContent,
  Table,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  TableBody,
  Tooltip,
  Tabs,
  Tab,
} from '@material-ui/core';
import { get, isEmpty, isNull, uniqueId } from 'lodash';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import moment from 'moment-timezone';
import CardTitle from 'containers/common/Card/CardTitle';
import { Container } from 'containers/CourtManagerAssignedClaims/styled-components';
import {
  StyledSahamTabs,
  EmptyBlocContainer,
} from 'containers/LawyerAssignmentsPage/styled-components';
import { translate } from 'containers/services/translate';
import { ROW_PER_PAGE, DEFAULT_PAGE_SIZE } from 'constants/global';
import { AssignedClaim } from 'containers/CourtManagerAssignedClaims/typings';
import messages from 'containers/LawyerAssignmentsPage/messages';
import { formatLossdate } from 'containers/CourtManagerAssignedClaims/utils';
import { requestLawyerAssignedClaims } from 'containers/LawyerAssignmentsPage/actions';
import {
  makeSelectLawyerAssignedClaims,
  makeSelectLawyerAssignedClaimsCount,
} from 'containers/LawyerAssignmentsPage/selectors';
import {
  displayByProp,
  displayStatus,
  displayMotif,
  formatAssignedClaims,
  setStep,
} from 'containers/LawyerAssignmentsPage/utils';
import { Listings } from 'containers/AppContainer/typings';
import {
  makeSelectKeycloakToken,
  makeSelectListings,
} from 'containers/AppContainer/selectors';
import { ASSIGNMENT_FIELDS } from 'containers/LawyerAssignmentsPage/constants';
import { pushTo } from 'containers/services/routes-helper';
import { PAGES } from 'routes';
import * as featureService from 'features/fflip-helpers';
import { FEATURES } from 'features/features';
import { CLAIM_STATUS } from 'containers/data-structure';
import { SEARCH_TYPE } from 'containers/ValidatedClaimsPage/constants';
import { requestSearch } from 'containers/ValidatedClaimsPage/actions';
import { getTabLabels } from 'containers/common/LegalProceedings/utils';
import NewSearchBar, { SearchQuery } from 'components/NewSearchBar';
import { SEARCH_QUERY_INIT_VALUES } from 'components/NewSearchBar/constants';
import emptyBox from 'images/emptyBox.svg';

export interface LawyerAssignmentsPageProps {
  intl: IntlShape;
  assignedClaims: AssignedClaim[];
  assignedClaimsCount: number;
  requestLawyerAssignedClaims: Function;
  listings: Listings;
  sagis_code: string | undefined;
  requestSearch: Function;
  location: { pathname: string };
  token: any;
}

export const LawyerAssignmentsPage: React.FC<LawyerAssignmentsPageProps> = (
  props,
): JSX.Element => {
  const [page, setPage] = useState(0);
  const [pageSize, setRowsPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [searchQuery, setSearchQuery] = React.useState<SearchQuery>(
    SEARCH_QUERY_INIT_VALUES,
  );
  const [tab, setTab] = React.useState(0);

  const assignedLawyer = props.sagis_code;

  const tableHeaders = [
    messages.claimNumber,
    messages.registrationNumber,
    messages.lossDate,
    messages.lossLocation,
    messages.lawyerAssignmentDate,
    messages.status,
  ];

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  /* istanbul ignore next */
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const size = parseInt(event.target.value, 10);
    setRowsPerPage(size);
  };
  const formattedAssignedClaims = formatAssignedClaims(
    props.assignedClaims,
    get(props.listings, 'villes', []),
  );

  const handleClick = (assignment: AssignedClaim) => () => {
    const claimId = assignment.claim?.id;

    if (!featureService.hasFeature(FEATURES.LAWYER_JUDGEMENT_ADD)) {
      return pushTo(`${PAGES.RECAP}/${claimId}`)();
    }

    return pushTo(`${PAGES.LEGAL_PROCEEDINGS}/${claimId}`)();
  };
  /* istanbul ignore next */
  const handlerSearch = () => {
    const query = {
      searchCriteria: {
        ...searchQuery,
        status: CLAIM_STATUS.VALIDATED,
        field: { assignedLawyer },
        isDefaultAssignement: !featureService.hasFeature(
          FEATURES.ASSIGNMENT_MULTIPLE_LAWYERS,
        ),
      },
      searchType: SEARCH_TYPE.ASSIGNMENT,
    };
    props.requestSearch(query, props.token);
  };
  /* istanbul ignore next */
  const handlerRefresh = () => {
    let procedureType = tab > 3 ? 'APPEAL' : 'FIRST_INSTANCE';
    setSearchQuery({
      ...SEARCH_QUERY_INIT_VALUES,
    });
    props.requestLawyerAssignedClaims(
      assignedLawyer,
      page,
      pageSize,
      setStep(tab),
      procedureType,
      !featureService.hasFeature(FEATURES.ASSIGNMENT_MULTIPLE_LAWYERS),
    );
  };

  const labels = getTabLabels(messages, props);
  const handleChange = (event: React.ChangeEvent<{}>, newTabIndex: number) => {
    setTab(newTabIndex);
    setPage(0);
  };
  useEffect(() => {
    let procedureType = tab > 3 ? 'APPEAL' : 'FIRST_INSTANCE';
    if (assignedLawyer) {
      props.requestLawyerAssignedClaims(
        assignedLawyer,
        page,
        pageSize,
        setStep(tab),
        procedureType,
        !featureService.hasFeature(FEATURES.ASSIGNMENT_MULTIPLE_LAWYERS),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignedLawyer, tab, page]);

  return (
    <Container>
      <Card>
        <CardContent>
          <div className="header">
            <CardTitle
              count={props.assignedClaimsCount}
              message={translate({ ...messages.tableTile }, props)}
            />
            {featureService.hasFeature(FEATURES.SEARCH_BAR) && (
              <NewSearchBar
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                handlerSearch={handlerSearch}
                handlerRefresh={handlerRefresh}
                isHomePage={false}
                showAll={true}
              />
            )}
          </div>
          {featureService.hasFeature(FEATURES.LEGAL_PROCEEDING_STEP) ? (
            <StyledSahamTabs>
              <Tabs
                value={tab}
                indicatorColor="primary"
                textColor="primary"
                onChange={handleChange}
                className="tab-container"
              >
                {labels.map((step: string) => (
                  <Tab label={step} data-testid="tab-2" />
                ))}
              </Tabs>
              {isEmpty(formattedAssignedClaims) ? (
                <EmptyBlocContainer>
                  <img
                    style={{ width: '29%' }}
                    src={emptyBox}
                    alt="empty box"
                  />
                  <span className="bloc-title">
                    {translate(messages.noDataTitle, props)}
                  </span>
                </EmptyBlocContainer>
              ) : (
                <>
                  <Table>
                    <TableHead>
                      <TableRow className="row">
                        {tableHeaders.map(header => (
                          <TableCell
                            key={uniqueId('header')}
                            align="left"
                            className="headerTitle"
                          >
                            {translate(header, props)}
                          </TableCell>
                        ))}
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {!isEmpty(formattedAssignedClaims) &&
                        formattedAssignedClaims
                          .filter((el: any) => !isNull(el))
                          .map(assignment => (
                            <TableRow
                              key={get(assignment, 'id')}
                              className={
                                !get(assignment, 'claim.id')
                                  ? 'disabled-click row'
                                  : 'row'
                              }
                              onClick={handleClick(assignment as AssignedClaim)}
                            >
                              <TableCell align="left">
                                {displayByProp(
                                  ASSIGNMENT_FIELDS.CLAIM_NUMBER,
                                  assignment as AssignedClaim,
                                )}
                              </TableCell>
                              <TableCell align="left">
                                {displayByProp(
                                  ASSIGNMENT_FIELDS.REGISTRATION_NUMBER,
                                  assignment as AssignedClaim,
                                )}
                              </TableCell>
                              <TableCell align="left">
                                {formatLossdate(
                                  displayByProp(
                                    ASSIGNMENT_FIELDS.LOSS_DATE,
                                    assignment as AssignedClaim,
                                  ),
                                  displayByProp(
                                    ASSIGNMENT_FIELDS.LOSS_HOUR,
                                    assignment as AssignedClaim,
                                  ),
                                )}
                              </TableCell>
                              <TableCell align="left">
                                {displayByProp(
                                  ASSIGNMENT_FIELDS.LOSS_LOCATION,
                                  assignment as AssignedClaim,
                                )}
                              </TableCell>
                              <TableCell align="left">
                                {moment(
                                  get(
                                    assignment,
                                    'lawyerAssignmentDate',
                                  ) as string,
                                ).format('DD/MM/YYYY')}
                              </TableCell>
                              <TableCell align="left">
                                {translate(
                                  get(
                                    messages,
                                    displayStatus(assignment as AssignedClaim),
                                  ),
                                  props,
                                )}
                              </TableCell>
                              <TableCell data-testid="rejectionReason">
                                {displayMotif(assignment as AssignedClaim) && (
                                  <Tooltip
                                    title={displayMotif(
                                      assignment as AssignedClaim,
                                    )}
                                  >
                                    <ErrorOutlineIcon className="rejection-reason-icon" />
                                  </Tooltip>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                    </TableBody>
                  </Table>
                  <TablePagination
                    rowsPerPageOptions={ROW_PER_PAGE}
                    component="div"
                    count={props.assignedClaimsCount}
                    rowsPerPage={pageSize}
                    page={page}
                    labelRowsPerPage={translate(
                      { ...messages.linesPerPage },
                      props,
                    )}
                    onChangePage={handleChangePage}
                    onChangeRowsPerPage={handleChangeRowsPerPage}
                  />
                </>
              )}
            </StyledSahamTabs>
          ) : (
            <>
              {isEmpty(formattedAssignedClaims) ? (
                <EmptyBlocContainer>
                  <img
                    style={{ width: '29%' }}
                    src={emptyBox}
                    alt="empty box"
                  />
                  <span className="bloc-title">
                    {translate(messages.noDataTitle, props)}
                  </span>
                </EmptyBlocContainer>
              ) : (
                <div>
                  <Table>
                    <TableHead>
                      <TableRow className="row">
                        {tableHeaders.map(header => (
                          <TableCell
                            key={uniqueId('header')}
                            align="left"
                            className="headerTitle"
                          >
                            {translate(header, props)}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {!isEmpty(formattedAssignedClaims) &&
                        formattedAssignedClaims.map(assignment => (
                          <TableRow
                            key={get(assignment, 'id')}
                            className={
                              !get(assignment, 'claim.id')
                                ? 'disabled-click row'
                                : 'row'
                            }
                            onClick={handleClick(assignment as AssignedClaim)}
                          >
                            <TableCell align="left">
                              {displayByProp(
                                ASSIGNMENT_FIELDS.CLAIM_NUMBER,
                                assignment as AssignedClaim,
                              )}
                            </TableCell>
                            <TableCell align="left">
                              {displayByProp(
                                ASSIGNMENT_FIELDS.REGISTRATION_NUMBER,
                                assignment as AssignedClaim,
                              )}
                            </TableCell>
                            <TableCell align="left">
                              {formatLossdate(
                                displayByProp(
                                  ASSIGNMENT_FIELDS.LOSS_DATE,
                                  assignment as AssignedClaim,
                                ),
                                displayByProp(
                                  ASSIGNMENT_FIELDS.LOSS_HOUR,
                                  assignment as AssignedClaim,
                                ),
                              )}
                            </TableCell>
                            <TableCell align="left">
                              {displayByProp(
                                ASSIGNMENT_FIELDS.LOSS_LOCATION,
                                assignment as AssignedClaim,
                              )}
                            </TableCell>
                            <TableCell align="left">
                              {moment(
                                get(
                                  assignment,
                                  'lawyerAssignmentDate',
                                ) as string,
                              ).format('DD/MM/YYYY')}
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                  <TablePagination
                    rowsPerPageOptions={ROW_PER_PAGE}
                    component="div"
                    count={props.assignedClaimsCount}
                    rowsPerPage={pageSize}
                    page={page}
                    labelRowsPerPage={translate(
                      { ...messages.linesPerPage },
                      props,
                    )}
                    onChangePage={handleChangePage}
                    onChangeRowsPerPage={handleChangeRowsPerPage}
                  />
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

const mapDispatchToProps = {
  requestLawyerAssignedClaims,
  requestSearch,
};
const mapStateToProps = createStructuredSelector({
  assignedClaims: makeSelectLawyerAssignedClaims(),
  assignedClaimsCount: makeSelectLawyerAssignedClaimsCount(),
  listings: makeSelectListings(),
  token: makeSelectKeycloakToken(),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(injectIntl(LawyerAssignmentsPage));
